import { styled } from '@mui/material'
import { PageArea, PageProps } from '..'
import { useProjectPrivateContext } from '../../context/projectContext'
import { projectPrivate } from '../../higher-order-components/projectPrivate'
import { VersionReportHeader } from './components/header'
import { VersionReportPage } from './components/pages/facade'
import { useCurrentVersion } from './hooks/currentReport'
import { useEvaluate } from './hooks/evaluate'
import { useDiscussionPanel } from './hooks/useDiscussionPanel'
import { useVersionSelectMenu } from './hooks/useVersionSelectMenu'
import { DiscussionPanel } from './components/discussion-panel/index'
import { SaveIndicatorProvider } from './components/save-indicator/SaveIndicatorProvider'
import { VersionSelectMenu } from './components/version-select-menu'
import { VersionConfigDialog } from './components/dialog/VersionConfigDialog'
import { Button } from '../../components/buttons'
import Loading, {
  useLoading,
} from '../../components/process-state-notifications/Loading'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import AlertDialog from '../../components/dialogs/AlertDialog'
import { doNotRequireSave, requireSave } from '../../../store/requiredSaveData'
import { useTemplateData } from './hooks/useTemplateData'
import { useReportData } from './hooks/useReportData'
import { useVersionReportVersionRepository } from '../../../services/adaptors/versionReportVersionRepositoryAdaptor'
import { intl } from '../../../i18n'
import { DiscussionProvider } from './context/DiscussionContext'
import { VersionReportVersionPageFormModel } from './components/dialog/VersionConfigDialog/model/VersionReportVersionPageFormModel'
import { colorPalette } from '../../style/colorPalette'

type ProjectVersionReportProps = PageProps
export const ProjectVersionReport = projectPrivate(
  ({ uuid }: ProjectVersionReportProps) => {
    const { projectUuid } = useProjectPrivateContext()
    const { isLoading, showLoading, hideLoading } = useLoading()
    const {
      currentPage,
      currentVersion,
      updateVersionUuid,
      updateCurrentVersion,
      storedVersionUuid,
      initialized,
      ...others
    } = useCurrentVersion(uuid, projectUuid)

    const { evaluate } = useEvaluate(currentVersion, currentPage)
    const discussionPanel = useDiscussionPanel()
    const {
      isOpen: isVersionSelectMenuOpen,
      toggle: toggleVersionSelectMenu,
      open: openVersionSelectMenu,
      close: closeVersionSelectMenu,
      selectReport,
      handleAfterCreateReport,
    } = useVersionSelectMenu(updateVersionUuid)

    const templateData = useTemplateData()
    const reportData = useReportData(templateData.selectedTemplate?.uuid)

    const handleAfterDeleteVersion = useCallback(async () => {
      updateVersionUuid(undefined)
      await reportData.refreshReports()
      openVersionSelectMenu()
    }, [openVersionSelectMenu, updateVersionUuid, reportData.refreshReports])

    const [isVersionConfigDialogOpen, setIsVersionConfigDialogOpen] =
      useState<boolean>(false)
    const [previewPage, setPreviewPage] =
      useState<VersionReportVersionPageFormModel | null>(null)
    const [hasUnsavedConfigChanges, setHasUnsavedConfigChanges] =
      useState<boolean>(false)
    const [discardConfirmOpen, setDiscardConfirmOpen] = useState<boolean>(false)
    const pendingCloseAction = useRef<(() => void) | null>(null)
    const { fetch: fetchVersion } = useVersionReportVersionRepository()

    const guardCloseVersionConfig = useCallback(
      (close: () => void) => {
        if (hasUnsavedConfigChanges) {
          pendingCloseAction.current = close
          setDiscardConfirmOpen(true)
          return
        }
        close()
      },
      [hasUnsavedConfigChanges]
    )

    const openVersionConfig = useCallback(() => {
      closeVersionSelectMenu()
      discussionPanel.close()
      setIsVersionConfigDialogOpen(true)
    }, [closeVersionSelectMenu, discussionPanel])

    const handleCloseVersionConfig = useCallback(() => {
      guardCloseVersionConfig(() => setIsVersionConfigDialogOpen(false))
    }, [guardCloseVersionConfig])

    const handleToggleVersionConfig = useCallback(() => {
      if (isVersionConfigDialogOpen) {
        guardCloseVersionConfig(() => setIsVersionConfigDialogOpen(false))
        return
      }
      openVersionConfig()
    }, [isVersionConfigDialogOpen, guardCloseVersionConfig, openVersionConfig])

    const handleAfterCreateReportWithInitial = useCallback(
      (uuid: string, isInitial: boolean) => {
        handleAfterCreateReport(uuid, isInitial)
        if (isInitial) {
          openVersionConfig()
        }
      },
      [handleAfterCreateReport, openVersionConfig]
    )

    const handleReportMenuToggle = useCallback(() => {
      if (!isVersionSelectMenuOpen && isVersionConfigDialogOpen) {
        guardCloseVersionConfig(() => {
          setIsVersionConfigDialogOpen(false)
          toggleVersionSelectMenu()
        })
        return
      }
      toggleVersionSelectMenu()
    }, [
      isVersionSelectMenuOpen,
      isVersionConfigDialogOpen,
      guardCloseVersionConfig,
      toggleVersionSelectMenu,
    ])

    const handleConfirmDiscard = useCallback(() => {
      setDiscardConfirmOpen(false)
      const action = pendingCloseAction.current
      pendingCloseAction.current = null
      action?.()
    }, [])

    const handleCancelDiscard = useCallback(() => {
      setDiscardConfirmOpen(false)
      pendingCloseAction.current = null
    }, [])

    const handleAfterSubmitVersionConfig = useCallback(async () => {
      if (!currentVersion) return
      const refreshed = await fetchVersion(currentVersion.uuid)
      if (refreshed) {
        updateCurrentVersion(refreshed)
      }
      await reportData.refreshReports()
      setIsVersionConfigDialogOpen(false)
    }, [
      currentVersion,
      fetchVersion,
      updateCurrentVersion,
      reportData.refreshReports,
    ])

    const handleAfterUpdateTemplate = useCallback(async () => {
      if (!currentVersion) return
      const refreshed = await fetchVersion(currentVersion.uuid)
      if (refreshed) {
        updateCurrentVersion(refreshed)
      }
    }, [currentVersion, fetchVersion, updateCurrentVersion])

    const handleAfterUpdateReport = useCallback(async () => {
      if (!currentVersion) return
      const refreshed = await fetchVersion(currentVersion.uuid)
      if (refreshed) {
        updateCurrentVersion(refreshed)
      }
    }, [currentVersion, fetchVersion, updateCurrentVersion])

    useEffect(() => {
      if (initialized && !storedVersionUuid) {
        openVersionSelectMenu()
      }
    }, [initialized, storedVersionUuid, openVersionSelectMenu])

    useEffect(() => {
      if (!isVersionConfigDialogOpen) {
        setPreviewPage(null)
      }
    }, [isVersionConfigDialogOpen])

    const dispatch = useDispatch()
    useEffect(() => {
      if (!hasUnsavedConfigChanges) return
      dispatch(requireSave())
      return () => {
        dispatch(doNotRequireSave())
      }
    }, [hasUnsavedConfigChanges, dispatch])

    const isPreviewMode = isVersionConfigDialogOpen && previewPage !== null

    const effectivePage = isPreviewMode ? previewPage : currentPage

    if (!initialized) {
      return (
        <PageArea>
          <Loading isLoading={true} />
        </PageArea>
      )
    }

    return (
      <PageArea>
        <DiscussionProvider
          discussionPanel={discussionPanel}
          versionPageUuid={currentPage?.uuid}
          currentVersionUuid={currentVersion?.uuid}
          templatePageUuid={currentPage?.templatePageUuid}
          versionList={reportData.reports}
        >
          <SaveIndicatorProvider>
            <Root>
              <VersionReportHeader
                currentPage={currentPage}
                currentVersion={currentVersion}
                {...others}
                evaluate={evaluate}
                discussionPanel={discussionPanel}
                isVersionSelectMenuOpen={isVersionSelectMenuOpen}
                onReportMenuToggle={handleReportMenuToggle}
                onAfterDeleteVersion={handleAfterDeleteVersion}
                isVersionConfigOpen={isVersionConfigDialogOpen}
                onToggleVersionConfig={handleToggleVersionConfig}
                showLoading={showLoading}
                hideLoading={hideLoading}
              />
              <MainContent>
                <VersionSelectMenu
                  projectUuid={projectUuid}
                  open={isVersionSelectMenuOpen}
                  onClose={closeVersionSelectMenu}
                  onReportSelect={selectReport}
                  onAfterCreateReport={handleAfterCreateReportWithInitial}
                  onAfterUpdateTemplate={handleAfterUpdateTemplate}
                  onAfterUpdateReport={handleAfterUpdateReport}
                  templateData={templateData}
                  reportData={reportData}
                />
                {currentVersion && isVersionConfigDialogOpen && (
                  <VersionConfigDialog
                    projectUuid={projectUuid}
                    open={isVersionConfigDialogOpen}
                    original={currentVersion}
                    onAfterSubmit={handleAfterSubmitVersionConfig}
                    onClose={handleCloseVersionConfig}
                    onPreviewPageChange={setPreviewPage}
                    onUnsavedChangesChange={setHasUnsavedConfigChanges}
                  />
                )}
                <PageContentArea
                  isDiscussionOpen={discussionPanel.isOpen}
                  isVersionConfigOpen={isVersionConfigDialogOpen}
                >
                  {currentVersion ? (
                    <>
                      {isVersionConfigDialogOpen && (
                        <PreviewBadge aria-label="プレビュー表示中">
                          <PreviewDot />
                          プレビュー
                        </PreviewBadge>
                      )}
                      <VersionReportPage
                        snapshotUuid={currentVersion?.snapshotUuid}
                        page={effectivePage}
                        takenAt={currentVersion?.takenAt}
                        isPreview={isVersionConfigDialogOpen}
                        showLoading={showLoading}
                        hideLoading={hideLoading}
                        {...others}
                      />
                    </>
                  ) : (
                    <TemplateSelectionContainer>
                      <TemplateSelectionContent>
                        <TemplateSelectionButtonContainer>
                          <Button onClick={openVersionSelectMenu}>
                            {intl.formatMessage({
                              id: 'versionReport.selectReport',
                            })}
                          </Button>
                        </TemplateSelectionButtonContainer>
                      </TemplateSelectionContent>
                    </TemplateSelectionContainer>
                  )}
                </PageContentArea>
                {currentPage && currentVersion && (
                  <DiscussionPanel projectUuid={projectUuid} />
                )}
              </MainContent>
              <Loading isLoading={isLoading} />
            </Root>
          </SaveIndicatorProvider>
        </DiscussionProvider>
        <AlertDialog
          isOpen={discardConfirmOpen}
          title="編集中の内容があります"
          message="保存されていない変更があります。破棄してよろしいですか？"
          submitButtonTitle="破棄"
          closeButtonTitle="キャンセル"
          submitHandler={handleConfirmDiscard}
          closeHandler={handleCancelDiscard}
        />
      </PageArea>
    )
  }
)
const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '16px',
  height: '100%',
})

const MainContent = styled('div')({
  display: 'flex',
  flex: 1,
  position: 'relative',
  overflow: 'hidden',
})

const PageContentArea = styled('div')<{
  isDiscussionOpen: boolean
  isVersionConfigOpen: boolean
}>(({ isDiscussionOpen, isVersionConfigOpen }) => ({
  position: 'relative',
  flex: 1,
  overflow: 'auto',
  ...(isDiscussionOpen && {
    paddingRight: '16px',
  }),
  ...(isVersionConfigOpen && {
    paddingLeft: '16px',
  }),
  transition: 'all 0.3s ease',
}))

const PreviewBadge = styled('div')({
  position: 'absolute',
  top: '14px',
  right: '18px',
  zIndex: 30,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '4px 10px',
  backgroundColor: colorPalette.yellow[0],
  border: `1px solid ${colorPalette.yellow[2]}`,
  borderRadius: '100px',
  color: colorPalette.monotone[10],
  fontSize: '11px',
  fontWeight: 600,
  pointerEvents: 'none',
  userSelect: 'none',
})

const PreviewDot = styled('span')({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: colorPalette.yellow[5],
})

const TemplateSelectionContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  width: '100%',
})

const TemplateSelectionContent = styled('div')({
  textAlign: 'center',
  padding: '40px',
  maxWidth: '400px',
})

const TemplateSelectionButtonContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
})
